import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { CLAIM_STAGES, StatusTimeline } from "@/components/StepProgress";
import { ArrowLeft, FileText, Image as ImageIcon, ShieldStar, Download } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function AdminClaimDetail() {
  const { claimId } = useParams();
  const navigate = useNavigate();
  const [claim, setClaim] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("admin_token")) navigate("/admin/login");
    api.get(`/claims/${claimId}`).then(({ data }) => setClaim(data)).catch(() => toast.error("Schaden nicht gefunden"));
  }, [claimId, navigate]);

  const updateStatus = async (newStatus) => {
    try {
      await api.patch(`/admin/claims/${claimId}/status`, { status: newStatus });
      const { data } = await api.get(`/claims/${claimId}`);
      setClaim(data);
      toast.success("Status aktualisiert");
    } catch {
      toast.error("Konnte Status nicht ändern");
    }
  };

  const downloadForm = async (key, label) => {
    try {
      const res = await api.get(`/admin/claims/${claimId}/forms/${key}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${label}-${claim.tracking_code}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${label} heruntergeladen`);
    } catch {
      toast.error("PDF konnte nicht erstellt werden");
    }
  };

  if (!claim) return <div className="min-h-screen flex items-center justify-center text-slate-500">Lade…</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center gap-4">
          <Button onClick={() => navigate("/admin")} variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
            <ArrowLeft size={18} className="mr-1.5" /> Zurück
          </Button>
          <div className="flex items-center gap-2">
            <ShieldStar size={20} weight="fill" />
            <div className="font-bold">Schadendetails</div>
          </div>
          <div className="ml-auto font-mono text-sm text-slate-300">{claim.tracking_code}</div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-3 gap-6">
        <section className="lg:col-span-2 space-y-6">
          <Card title="Kunde">
            <Grid>
              <Cell label="Name">{claim.customer_name || "—"}</Cell>
              <Cell label="E-Mail">{claim.customer_email || "—"}</Cell>
              <Cell label="Telefon">{claim.customer_phone || "—"}</Cell>
              <Cell label="Tracking-Code">{claim.tracking_code}</Cell>
            </Grid>
          </Card>

          <Card title="Fahrzeug">
            <Grid>
              <Cell label="Kennzeichen">{claim.license_plate || "—"}</Cell>
              <Cell label="Hersteller">{claim.manufacturer || "—"}</Cell>
              <Cell label="Modell">{claim.model || "—"}</Cell>
              <Cell label="Baujahr">{claim.year || "—"}</Cell>
              <Cell label="VIN">{claim.vin || "—"}</Cell>
              <Cell label="HSN/TSN">{claim.hsn || "—"} / {claim.tsn || "—"}</Cell>
            </Grid>
          </Card>

          <Card title="Schaden">
            <Grid>
              <Cell label="Scheibe">{claim.glass_position || "—"}</Cell>
              <Cell label="Art">{claim.damage_type || "—"}</Cell>
              <Cell label="Notizen" full>{claim.damage_notes || "—"}</Cell>
            </Grid>
          </Card>

          {claim.flow_type === "insurance" ? (
            <Card title="Versicherung">
              <Grid>
                <Cell label="Gesellschaft">{claim.insurance_company || "—"}</Cell>
                <Cell label="Art">{claim.insurance_type || "—"}</Cell>
                <Cell label="Selbstbeteiligung">{claim.deductible ? `${claim.deductible} €` : "—"}</Cell>
                <Cell label="Schadennummer">{claim.claim_number || "—"}</Cell>
              </Grid>
            </Card>
          ) : (
            <Card title="Selbstzahler-Schätzung">
              <Grid>
                <Cell label="Schätzung niedrig">{claim.estimated_price_low ? `${claim.estimated_price_low} €` : "—"}</Cell>
                <Cell label="Schätzung hoch">{claim.estimated_price_high ? `${claim.estimated_price_high} €` : "—"}</Cell>
                <Cell label="Bestätigter Preis">{claim.confirmed_price ? `${claim.confirmed_price} €` : "Ausstehend"}</Cell>
                <Cell label="Anzahlung">{claim.deposit_paid ? <Badge className="bg-emerald-500">Bezahlt — {claim.deposit_amount} €</Badge> : <Badge variant="outline">Offen</Badge>}</Cell>
              </Grid>
            </Card>
          )}

          <Card title="Termin">
            <Grid>
              <Cell label="Datum">{claim.appointment_date ? new Date(claim.appointment_date).toLocaleDateString("de-DE") : "—"}</Cell>
              <Cell label="Uhrzeit">{claim.appointment_time || "—"}</Cell>
              <Cell label="Signatur">{claim.signed ? <Badge className="bg-emerald-500">Unterzeichnet</Badge> : <Badge variant="outline">Offen</Badge>}</Cell>
            </Grid>
          </Card>

          <Card title="Dokumente & Fotos">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(claim.files || []).map((f, idx) => (
                <a
                  key={idx}
                  href={fileUrl(f.storage_path)}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-xl border border-slate-200 hover:border-blue-400 bg-white overflow-hidden"
                  data-testid={`file-${f.category}-${idx}`}
                >
                  {f.content_type?.startsWith("image/") ? (
                    <img src={fileUrl(f.storage_path)} alt={f.category} className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="w-full aspect-square bg-slate-50 flex items-center justify-center">
                      <FileText size={36} className="text-slate-400" />
                    </div>
                  )}
                  <div className="p-2 text-xs">
                    <div className="font-medium text-slate-900 truncate">{f.category}</div>
                    <div className="text-slate-500 truncate">{f.original_filename}</div>
                  </div>
                </a>
              ))}
              {claim.signature_path && (
                <a
                  href={fileUrl(claim.signature_path)}
                  target="_blank"
                  rel="noreferrer"
                  className="block rounded-xl border border-slate-200 hover:border-blue-400 bg-white overflow-hidden"
                  data-testid="file-signature"
                >
                  <img src={fileUrl(claim.signature_path)} alt="Signature" className="w-full aspect-square object-contain bg-white" />
                  <div className="p-2 text-xs">
                    <div className="font-medium text-slate-900">Unterschrift</div>
                  </div>
                </a>
              )}
              {(claim.files || []).length === 0 && !claim.signature_path && (
                <div className="col-span-full text-center py-8 text-slate-500">
                  <ImageIcon size={32} className="mx-auto text-slate-300 mb-2" />
                  Keine Dateien hochgeladen.
                </div>
              )}
            </div>
          </Card>
        </section>

        <aside className="space-y-6">
          <Card title="Status">
            <div className="space-y-4">
              <Select value={claim.status} onValueChange={updateStatus}>
                <SelectTrigger className="h-12" data-testid="admin-status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CLAIM_STAGES.map((s) => (
                    <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div className="pt-2">
                <StatusTimeline stages={CLAIM_STAGES} currentStatus={claim.status} history={claim.status_history || []} />
              </div>
            </div>
          </Card>

          <Card title="PDF-Archiv">
            {claim.flow_type === "insurance" ? (
              <div className="space-y-2">
                {[
                  ["kasko", "Kasko-Schadenanzeige"],
                  ["kundenrabatt", "Kundenrabatt"],
                  ["abtretung", "Abtretungserklärung"],
                ].map(([key, label]) => (
                  <Button
                    key={key}
                    variant="outline"
                    className="w-full justify-start"
                    onClick={() => downloadForm(key, label)}
                    data-testid={`download-form-${key}`}
                  >
                    <Download size={16} className="mr-2 text-blue-600" /> {label}
                  </Button>
                ))}
                {!claim.signed && (
                  <p className="text-xs text-slate-500 pt-1">
                    Noch keine Kundenunterschrift — PDFs werden ohne Unterschrift erstellt.
                  </p>
                )}
              </div>
            ) : claim.invoice_number ? (
              <Button
                variant="outline"
                className="w-full justify-start"
                onClick={() => downloadForm("rechnung", `Rechnung-${claim.invoice_number}`)}
                data-testid="download-form-rechnung"
              >
                <Download size={16} className="mr-2 text-blue-600" /> Rechnung {claim.invoice_number}
              </Button>
            ) : (
              <p className="text-sm text-slate-500">Noch keine Rechnung vorhanden (wird nach Anzahlung erstellt).</p>
            )}
          </Card>

          <Card title="Erstellt">
            <div className="text-sm text-slate-700">{new Date(claim.created_at).toLocaleString("de-DE")}</div>
            <div className="text-xs text-slate-500 mt-1">Aktualisiert: {new Date(claim.updated_at).toLocaleString("de-DE")}</div>
          </Card>
        </aside>
      </main>
    </div>
  );
}

const Card = ({ title, children }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
    <h3 className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-3">{title}</h3>
    {children}
  </div>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">{children}</div>;

const Cell = ({ label, children, full }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{label}</div>
    <div className="text-sm text-slate-900 font-medium mt-0.5">{children}</div>
  </div>
);
